import {
  ChangeDetectionStrategy,
  Component,
  inject,
  linkedSignal,
  viewChild,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";

import { OrgFacade } from "state/org.facade";
import { UserFacade } from "state/user.facade";

import { map } from "rxjs";
import { DropdownContentDirective } from "../dropdown/dropdown-content.directive";
import { DropdownComponent } from "../dropdown/dropdown.component";
import {
  DevOrgSelectorComponent,
  OrgEntry,
} from "./dev-org-selector/dev-org-selector.component";

type RedirectConfig = {
  orgId?: string;
  route?: string[];
};

@Component({
  selector: "dev-org-dropdown",
  templateUrl: "./dev-org-dropdown.component.html",
  styleUrls: ["./dev-org-dropdown.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [DropdownComponent, DropdownContentDirective, DevOrgSelectorComponent],
  host: {
    class: "inseer-ds",
  },
})
export class DevOrgDropdownComponent {
  private readonly orgFacade = inject(OrgFacade);
  private readonly userFacade = inject(UserFacade);
  private readonly router = inject(Router);

  private readonly userOrgId = toSignal<string | null>(
    this.userFacade.user$.pipe(map((user) => user?.organization ?? null)),
    { initialValue: null }
  );

  private readonly activeOrgName = toSignal<string, string>(
    this.orgFacade.orgName$.pipe(map((orgName) => orgName ?? "")),
    { initialValue: "" }
  );

  protected readonly selectedOrgName = linkedSignal<string>(() => this.activeOrgName());

  private readonly menu = viewChild(DropdownComponent);

  protected onOrgSelected(org: OrgEntry): void {
    this.selectedOrgName.set(org.name);
    this.redirectToOrg(org._id);
    this.menu()?.close();
  }

  private readonly ROUTES_UNSUPPORTED_IN_DEV_MODE = [
    "dev",
    "learn",
    "video-library",
    "help",
  ];

  private redirectToOrg(orgId: string): void {
    const redirectConfig = ((): RedirectConfig => {
      if (orgId === this.userOrgId()) {
        // User's own org, drop the orgId from the query params
        return { orgId: undefined };
      }

      const url = this.router.url.slice(1); // Remove leading slash
      const currentRouteNotSupportedInDevMode = this.ROUTES_UNSUPPORTED_IN_DEV_MODE.some(
        (route) => url.startsWith(route)
      );

      // If the current route is not supported in dev mode, redirect to the default page
      if (currentRouteNotSupportedInDevMode) {
        return { orgId, route: ["homepage"] };
      }

      // Otherwise, stay on the current route and just update the orgId in the query params
      return { orgId };
    })();

    this.router.navigate(redirectConfig.route ?? [], {
      queryParams: { orgId: redirectConfig.orgId },
      queryParamsHandling: "merge",
    });
  }
}
